import { CustomColDef } from '@shared/components/ui/AgGridTable';
import { Shop as ShopComponent } from '@shared/components/ui/shop';
import { formatCurrencyAmount, useCurrency } from '@modules/Orders/shared/hooks/useCurrency';
import { ColumnDef } from '@tanstack/react-table';
import { ColDef } from 'ag-grid-community';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { RefundOrder } from '../models/RefundOrder';

const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  } catch {
    return '-';
  }
};

const convertToTanStackColumns = (agGridColumns: ColDef[]): ColumnDef<RefundOrder>[] => {
  return agGridColumns
    .filter((col) => {
      const field = col.field || col.headerName || 'unknown';
      return field !== 'unknown';
    })
    .map((col) => ({
      id: col.field || col.headerName || 'unknown',
      accessorKey: col.field || col.headerName || 'unknown',
      header: col.headerName || 'Columna',
    }));
};

interface UseAgGridColumnsProps {
  selectedColumns?: string[];
}

export const useAgGridColumns = ({ selectedColumns = [] }: UseAgGridColumnsProps) => {
  const { t } = useTranslation('refundsModule');
  const currency = useCurrency();

  const allColumnDefs: ColDef[] = useMemo(() => {
    const baseColumns: CustomColDef[] = [
      {
        field: 'order_ref',
        headerName: t('table.columns.order_ref'),
        minWidth: 180,
        cellRenderer: (params: any) => {
          const order = params.data as RefundOrder;
          return <div className="text-sm font-medium text-title">{order.order_ref || '-'}</div>;
        },
      },
      {
        field: 'shop_name',
        headerName: t('table.columns.shop_name'),
        minWidth: 160,
        valueGetter: (params) => params.data?.shop_name,
        cellRenderer: (params: any) => {
          const order = params.data as RefundOrder;
          if (!order.shop_name) return <span className="text-sm text-muted-foreground">-</span>;
          return <ShopComponent name={order.shop_name} />;
        },
      },
      {
        field: 'total',
        headerName: t('table.columns.total'),
        width: 120,
        minWidth: 100,
        cellRenderer: (params: any) => {
          const order = params.data as RefundOrder;
          return (
            <div className="text-sm text-title tabular-nums">
              {order.total != null ? formatCurrencyAmount(order.total, currency) : '-'}
            </div>
          );
        },
      },
      {
        field: 'delivered_at',
        headerName: t('table.columns.delivered_at'),
        width: 140,
        minWidth: 120,
        cellRenderer: (params: any) => {
          const order = params.data as RefundOrder;
          return <div className="text-sm text-complementary-text tabular-nums">{formatDate(order.delivered_at)}</div>;
        },
      },
      {
        field: 'status',
        headerName: t('table.columns.status'),
        width: 140,
        minWidth: 120,
        cellRenderer: (params: any) => {
          const order = params.data as RefundOrder;
          if (!order.status) return <span className="text-sm text-muted-foreground">-</span>;
          const isPending = order.status === 'pending';
          return (
            <span
              className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                isPending ? 'bg-bg-warning text-warning' : 'bg-bg-info text-info'
              }`}
            >
              {t(`table.status.${order.status}`)}
            </span>
          );
        },
      },
      {
        field: 'sum_amount',
        headerName: t('table.columns.sum_amount'),
        width: 140,
        minWidth: 100,
        cellRenderer: (params: any) => {
          const order = params.data as RefundOrder;
          return (
            <div className="text-sm text-complementary-text tabular-nums">
              {order.sum_amount != null ? formatCurrencyAmount(order.sum_amount, currency) : '-'}
            </div>
          );
        },
      },
    ];

    return baseColumns;
  }, [t, currency]);

  const filteredColumnDefs: ColDef[] = useMemo(() => {
    const dedupeByField = (cols: ColDef[]) => {
      const seen = new Set<string>();
      return cols.filter((c) => {
        const id = (c.field || c.headerName || 'unknown') as string;
        if (seen.has(id)) return false;
        seen.add(id);
        return true;
      });
    };

    if (selectedColumns.length === 0) {
      return dedupeByField(allColumnDefs);
    }

    return allColumnDefs.filter((col) => {
      const fieldId = col.field || col.headerName || 'unknown';
      return selectedColumns.includes(fieldId);
    });
  }, [allColumnDefs, selectedColumns]);

  const tanStackColumns: ColumnDef<RefundOrder>[] = useMemo(() => {
    return convertToTanStackColumns(allColumnDefs);
  }, [allColumnDefs]);

  return { allColumnDefs, filteredColumnDefs, tanStackColumns };
};
